import { useState, type KeyboardEvent } from 'react';
import { Autocomplete, Box, Chip, CircularProgress, InputBase, Typography } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useCodeSearch } from '../hooks/useCodeSearch';
import type { Code, CodeType } from '../services/codesService';

export interface ChipInputProps {
  /** Current codes shown as chips. */
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Hint shown beneath the field (e.g. "Press Enter or comma to add each code"). */
  helperText?: string;
  /** Accessible label for the typing input. */
  ariaLabel?: string;
  error?: boolean;
  disabled?: boolean;
  /**
   * When set, the typing input offers code suggestions from the local Quick Base mirror
   * (feature 046). Omit it and this component behaves exactly as it did before that feature:
   * free text, no dropdown, no network call.
   */
  codeType?: CodeType;
}

/**
 * Free-text multi-chip input (feature 034) used for ICD-10 codes and CPT/ASA modifiers.
 * Type a value and press Enter or comma to add it as a removable chip; Backspace on an
 * empty input removes the last chip. Duplicates are intentionally allowed (no
 * de-duplication) per spec 034. There is no option list — entries are free text.
 */
export default function ChipInput({
  value,
  onChange,
  placeholder,
  helperText,
  ariaLabel = 'Add code',
  error,
  disabled,
  codeType,
}: ChipInputProps) {
  const [draft, setDraft] = useState('');
  // Same reason as CodeAutocomplete: MUI decides whether to open the listbox at the moment of the
  // input event, so with an async source the options arrive after it has already declined and the
  // dropdown lags a keystroke behind. Driving `open` ourselves opens it when there is something
  // to show. The draft is always typed here — chips are the committed value — so there is no
  // seeded-value case to guard against.
  const [popupOpen, setPopupOpen] = useState(false);
  // The typing input is only the space the chips have left over, so a dropdown sized to it — MUI's
  // default — is as narrow as whatever is left and shows no description at all. The field as a
  // whole is what the biller sees as the field, so that is what the dropdown is sized to. Held in
  // state, not a ref, so it is on hand on the render after mount rather than a render later.
  const [fieldEl, setFieldEl] = useState<HTMLElement | null>(null);
  const { codes, loading, status } = useCodeSearch(codeType, draft);

  // Suggestions are an addition to this control, never a gate on it: every existing behaviour —
  // Enter/comma commit, Backspace delete, duplicates allowed, free text — is untouched, and the
  // dropdown simply does not exist when the set cannot serve one.
  const suggestionsAvailable = !!codeType && status === 'READY' && codes.length > 0;

  const commit = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    onChange([...value, trimmed]);
    setDraft('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const removeAt = (index: number) => onChange(value.filter((_, i) => i !== index));

  return (
    <Box>
      <Box
        ref={setFieldEl}
        sx={{
          position: 'relative',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.75,
          minHeight: 36,
          px: 1.5,
          py: 0.75,
          borderRadius: '8px',
          bgcolor: 'background.paper',
        }}
      >
        {/* Border overlay — position:absolute so it doesn't contribute to box-model height,
            matching MUI OutlinedInput's NotchedOutline fieldset approach */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            border: '1px solid',
            borderColor: error ? 'error.main' : 'divider',
            borderRadius: 'inherit',
            pointerEvents: 'none',
          }}
        />
        {value.map((code, i) => (
          <Chip
            key={`${code}-${i}`}
            label={code}
            size="small"
            onDelete={disabled ? undefined : () => removeAt(i)}
            deleteIcon={<CancelIcon aria-label={`Remove ${code}`} />}
          />
        ))}
        {codeType ? (
          <Autocomplete<Code, false, false, true>
            freeSolo
            openOnFocus={false}
            open={popupOpen && suggestionsAvailable}
            onOpen={() => setPopupOpen(true)}
            onClose={() => setPopupOpen(false)}
            disabled={disabled}
            options={suggestionsAvailable ? codes : []}
            // The server already filtered and ranked.
            filterOptions={(x) => x}
            loading={loading}
            inputValue={draft}
            onInputChange={(_e, next, reason) => {
              // 'reset' fires when an option is picked; commit() has already handled that and
              // cleared the draft, so echoing MUI's value back here would refill the input.
              if (reason !== 'reset') setDraft(next);
            }}
            onChange={(_e, selected) => {
              // Selecting commits a chip carrying the CODE only, never code — description.
              setPopupOpen(false);
              commit(typeof selected === 'string' ? selected : (selected?.code ?? ''));
            }}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.code)}
            isOptionEqualToValue={(option, selected) => option.code === selected.code}
            renderOption={(props, option) => {
              const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key?: string };
              return (
                <Box component="li" key={key} {...rest} sx={{ display: 'block !important', py: 0.75 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary' }}>
                    {option.code}
                  </Typography>
                  {/* CSS truncation with the full text left in the DOM — see CodeAutocomplete. */}
                  <Typography
                    title={option.description}
                    sx={{
                      fontSize: 12,
                      color: 'text.secondary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {option.description}
                  </Typography>
                </Box>
              );
            }}
            sx={{ flex: 1, minWidth: 80 }}
            slotProps={{
              listbox: { 'aria-busy': loading },
              // Both halves are needed: MUI writes the typing input's own width onto the popper as
              // an inline style, so re-pointing the anchor alone would move the listbox under the
              // field without widening it.
              ...(fieldEl && {
                popper: { anchorEl: fieldEl, style: { width: fieldEl.clientWidth } },
              }),
            }}
            renderInput={(params) => (
              <InputBase
                {...params.InputProps}
                inputProps={{ ...params.inputProps, 'aria-label': ariaLabel }}
                disabled={disabled}
                // Keydown stays on this component, so Enter/comma commit and Backspace-delete
                // behave exactly as before. MUI handles Enter itself only while an option is
                // highlighted, which is the one case where selecting should win.
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                sx={{ flex: 1, minWidth: 80, fontSize: 14 }}
                endAdornment={loading ? <CircularProgress color="inherit" size={14} /> : null}
              />
            )}
          />
        ) : (
          <InputBase
            value={draft}
            disabled={disabled}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            inputProps={{ 'aria-label': ariaLabel }}
            sx={{ flex: 1, minWidth: 80, fontSize: 14 }}
          />
        )}
      </Box>
      {helperText && (
        <Typography
          variant="caption"
          sx={{ color: error ? 'error.main' : 'text.secondary', mt: 0.5, display: 'block' }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
